import React from 'react';
import type { ResultDraw } from '../../utils/dashboard/newResults';

/**
 * The draw, named as an event: what was taken, when, and by whom.
 *
 * The results page used to be organised around how data arrived — "Reports and
 * documents", "Wearables and exports", "Add biomarker values", and only then
 * the member's actual numbers. A member who had just had blood taken arrived
 * looking for a thing that happened to them on a Tuesday and found a filing
 * system. This card is the answer to "is this the test I just did?", which is
 * the question they came with.
 *
 * Counts are stated and nothing is concluded from them. "3 outside range" is a
 * fact about a reference interval; whether it matters is the clinician's call
 * and lives in the report.
 */

function formatDrawDate(draw: ResultDraw): string {
  // The collection date is the one the member was present for. Fall back to the
  // observation date only when the lab did not report a draw time.
  const date = draw.collectedAt || draw.observedAt;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export interface ResultsDrawHeaderProps {
  draw: ResultDraw;
  /** True when this is the member's only draw — their baseline. */
  isFirstDraw: boolean;
  /** Total markers shown in the list below, across every draw on file. */
  totalMarkersOnFile: number;
  /**
   * Whether any row below actually carries a "New" tag. The list suppresses
   * them when every row would be tagged, and this copy must not promise a cue
   * that is not there.
   */
  tagsShown?: boolean;
}

export default function ResultsDrawHeader({
  draw,
  isFirstDraw,
  totalMarkersOnFile,
  tagsShown = false,
}: ResultsDrawHeaderProps) {
  const provenance = [draw.panelLabel, formatDrawDate(draw), draw.labLabel]
    .filter(Boolean)
    .join(' · ');

  const flagged = draw.outOfRangeCount > 0;

  return (
    <section
      className="border p-6 md:p-8 mb-6"
      aria-labelledby="draw-heading"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
    >
      <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--melio-olive-deep)' }}>
        {isFirstDraw ? 'Your first results' : 'Most recent results'}
      </p>
      <h2
        id="draw-heading"
        className="text-2xl md:text-3xl font-normal mb-2"
        style={{ fontFamily: 'var(--melio-display)', color: 'var(--melio-charcoal)' }}
      >
        {provenance}
      </h2>

      <div className="flex items-center gap-2 flex-wrap mt-4">
        <span
          className="inline-flex items-center rounded-full text-[11px] font-semibold"
          style={{
            color: flagged ? '#9B2F2A' : '#4F5D18',
            backgroundColor: flagged ? 'rgba(155, 47, 42, 0.14)' : 'rgba(79, 93, 24, 0.18)',
            padding: '3px 12px',
          }}
        >
          {flagged ? `${draw.outOfRangeCount} outside range` : 'All in range'}
        </span>
        <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
          {draw.count === 1 ? '1 marker' : `${draw.count} markers`} came back on this draw
          {draw.notComparedCount > 0
            ? `, ${draw.notComparedCount} of which we can't compare to a range`
            : ''}
          .
        </span>
      </div>

      {isFirstDraw ? (
        <p className="text-sm leading-relaxed mt-4" style={{ color: 'var(--melio-text-muted)' }}>
          This is your baseline. There is nothing to compare it against yet — your next panel is
          what turns these numbers into a direction of travel.
        </p>
      ) : (
        <p className="text-sm leading-relaxed mt-4" style={{ color: 'var(--melio-text-muted)' }}>
          Below is the most recent reading for every marker on your record
          {totalMarkersOnFile > draw.count
            ? ` — ${totalMarkersOnFile} in total, including earlier draws`
            : ''}
          .{tagsShown ? ' Markers from this draw are tagged New.' : ''}
        </p>
      )}
    </section>
  );
}
